/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import Dotdotdot from 'react-dotdotdot';
import classNames from 'classnames';
import Icon from 'components/Icon';
import CallSign from 'components/CallSign';
import {
  redirectToExternalUrl,
  helperGoToRelativeUrl,
  LAST_SESSION_PATH,
} from 'helpers/RouteHelper';
import { setSessionStorageItem } from 'helpers/Storage';
import { isInternalLink } from 'helpers/ContentHelper';
import { SegmentUiElements } from '@foxcorp/delta-web-analytics';

// const { PLAY, SERIES_EPISODE, SERIES_TITLE } = SegmentUiElements;
const READER_MS_DELAY = 2300;
const TileTitle = ({
  account,
  gridIndex,
  gridTitle,
  itemIndex,
  isDisabled,
  isHoverStateV2,
  isRedirect,
  isSeriesDetailAvailable,
  isUpcoming,
  link,
  name,
  network,
  onTileClick,
  onTitleClick,
  originalItem,
  percentWatched,
  recommendationIdData,
  subtitleText,
  target,
  theme,
  title,
  titleLines,
  titleText,
}) => {
  const titleRef = useRef(null);
  useEffect(() => {
    const ref = setTimeout(() => {
      if (titleRef.current) {
        titleRef.current.ariaLabel = titleLines
          ? title?.props?.children?.props?.children?.[0]
          : title;
        titleRef.current.ariaHidden = false;
      }
    }, READER_MS_DELAY);
    return () => {
      clearTimeout(ref);
    };
  }, [title, titleLines]);

  let titleLink = link;
  const titleClasses = [theme.title];
  const titleClass = isDisabled ? classNames([theme.title, theme.disabled]) : theme.title;
  if (!title || title.length === 0) return null;
  const text = titleLines ? (
    <div className={theme.ellipsisTitle} data-test="dotdotdot-container">
      <Dotdotdot clamp={titleLines}>{title}</Dotdotdot>
    </div>
  ) : (
    title
  );
  if (!link || isDisabled) return <div className={titleClass}>{text}</div>;
  if (isRedirect) {
    titleLink = null;
    titleClasses.push(theme.redirectLink);
  }
  const onClick = () => {
    setSessionStorageItem({ key: LAST_SESSION_PATH, value: window.location.pathname });
    const commonArgs = {
      account,
      gridIndex,
      gridTitle,
      itemIndex,
      name,
      network,
      originalItem,
      percentWatched,
      recommendationIdData,
      subtitleText,
      title,
      titleText,
    };
    onTileClick({ ...commonArgs, uiElement: SegmentUiElements.SERIES_EPISODE });
    if (onTitleClick) {
      onTitleClick({ ...commonArgs, uiElement: SegmentUiElements.SERIES_TITLE });
    }
  };
  if (isHoverStateV2) {
    if (isUpcoming && isSeriesDetailAvailable === false) {
      titleLink = null;
    } else {
      titleClasses.push(theme.titleV2);
    }
  }

  function linkHander(e) {
    onClick(e);
  }

  return (
    <Link
      to={titleLink}
      onClick={linkHander}
      className={classNames(titleClasses)}
      tab-index="0"
      ref={titleRef}
      aria-hidden="true"
      aria-label={name}
    >
      {text}
    </Link>
  );
};

const TilePlayButton = ({ theme, playButton, text }) => {
  if (!playButton) return null;
  return (
    <div className={theme.play}>
      <Icon icon="play-button" color="white" size="full" />
      {text && <div className={theme.playText}>{text}</div>}
    </div>
  );
};

const TileNetworkIcon = ({ theme, networkIcon, showNetworkIcon, isHoverStateV2 }) => {
  if (!networkIcon || !showNetworkIcon) return null;
  const networkIconClasses = [theme.networkIcon, theme.dogEar];
  if (isHoverStateV2) networkIconClasses.push(theme.networkIconHoverStateV2);
  return (
    <div className={classNames(networkIconClasses)}>
      <img alt="Network Icon" src={networkIcon} />
    </div>
  );
};

const TileMetaData = ({ theme, metadata }) => {
  if (!metadata || metadata.length === 0) return null;
  const maxMetaDataLines = 2;
  return (
    <Dotdotdot clamp={maxMetaDataLines}>
      <p className={theme.metadata}>{metadata}</p>
    </Dotdotdot>
  );
};

const TileUpsellCTA = ({
  display,
  theme,
  upsellButtonText,
  upsellButtonIcon,
  upsellButtonClick,
}) => {
  if (!display) return null;
  return (
    <button className={theme.networkUpsellCTA} onClick={upsellButtonClick}>
      <span>{upsellButtonText}</span>
      {upsellButtonIcon}
    </button>
  );
};

const TileUpsellDogEar = ({ theme, image, text }) => (
  <div className={`${theme.networkIcon} ${theme.dogEar}`}>
    <img alt={text} src={image} />
    <span>{text}</span>
  </div>
);

const TileNetworkBubble = ({ callSign, theme, link, showNetworkIcon }) => {
  if (!callSign || !showNetworkIcon) return null;
  const networkLogoStyle = [theme.networkLogoBubble];
  const navigateToURL = () => {
    if (isInternalLink(link)) {
      helperGoToRelativeUrl(link);
    }
    redirectToExternalUrl(link);
  };
  return (
    <Link onClick={navigateToURL} className={theme.networkLogoBubbleContainer}>
      <div className={classNames(networkLogoStyle)}>
        <div className={theme.networkLogoBubbleIcon}>
          <CallSign callSign={callSign} color="white" />
        </div>
      </div>
    </Link>
  );
};

export {
  TileUpsellCTA,
  TileTitle,
  TilePlayButton,
  TileNetworkIcon,
  TileMetaData,
  TileUpsellDogEar,
  TileNetworkBubble,
};
